/**
 * Etiquetas y formateadores de Asignaciones de packs/membresías (US-2.4, TG-53).
 *
 * El copy vive en español (es-CO). Los estados se pintan con color + icono + texto
 * (nunca solo color). El backend ya deriva `EXPIRED` por fecha en lectura, así que
 * aquí no se recalcula el estado: solo se traduce el enum del contrato.
 */
import type {
  MembershipAssignmentStatus,
  PackAssignmentStatus,
} from '@goelectric/shared-types';

/** Etiqueta visible de cada estado de asignación de pack. */
export const PACK_ASSIGNMENT_STATUS_LABELS: Record<PackAssignmentStatus, string> = {
  ACTIVE: 'Activo',
  EXHAUSTED: 'Agotado',
  EXPIRED: 'Vencido',
  CANCELLED: 'Cancelado',
};

/** Descripción del estado de la asignación de pack para tooltips/ayudas. */
export const PACK_ASSIGNMENT_STATUS_DESCRIPTIONS: Record<PackAssignmentStatus, string> = {
  ACTIVE: 'El pack está vigente y disponible para consumir kWh.',
  EXHAUSTED: 'El cliente consumió todos los kWh del pack.',
  EXPIRED: 'La vigencia del pack terminó.',
  CANCELLED: 'La asignación fue cancelada por un administrador.',
};

/** Etiqueta visible de cada estado de asignación de membresía. */
export const MEMBERSHIP_ASSIGNMENT_STATUS_LABELS: Record<MembershipAssignmentStatus, string> = {
  ACTIVE: 'Activa',
  EXPIRED: 'Vencida',
  CANCELLED: 'Cancelada',
};

/** Descripción del estado de la asignación de membresía. */
export const MEMBERSHIP_ASSIGNMENT_STATUS_DESCRIPTIONS: Record<
  MembershipAssignmentStatus,
  string
> = {
  ACTIVE: 'La membresía está vigente para el cliente.',
  EXPIRED: 'El periodo de la membresía terminó.',
  CANCELLED: 'La membresía fue cancelada o reemplazada por otra.',
};

/** `true` si la asignación cuenta como vigente para las secciones principales. */
export function isActivePackAssignment(status: PackAssignmentStatus): boolean {
  return status === 'ACTIVE';
}

/** `true` si la asignación de membresía está activa. */
export function isActiveMembershipAssignment(status: MembershipAssignmentStatus): boolean {
  return status === 'ACTIVE';
}

/** Formatea kWh con separador es-CO y sufijo de unidad (`1.250,5 kWh`). */
export function formatKwh(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return `${new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)} kWh`;
}

/**
 * Saldo restante del pack = kWh totales − consumidos (Glosario). Se acota a 0 para
 * no mostrar negativos ante datos inconsistentes; el backend ya valida el rango.
 */
export function formatRemainingKwh(
  total: number | null | undefined,
  consumed: number | null | undefined,
): string {
  if (
    total === null ||
    total === undefined ||
    consumed === null ||
    consumed === undefined ||
    Number.isNaN(total) ||
    Number.isNaN(consumed)
  ) {
    return '—';
  }
  return formatKwh(Math.max(total - consumed, 0));
}

/** Detalle secundario del consumo: `20 kWh de 50 kWh consumidos`. */
export function formatConsumedDetail(
  total: number | null | undefined,
  consumed: number | null | undefined,
): string {
  if (
    total === null ||
    total === undefined ||
    consumed === null ||
    consumed === undefined ||
    Number.isNaN(total) ||
    Number.isNaN(consumed)
  ) {
    return '—';
  }
  return `${formatKwh(Math.max(consumed, 0))} de ${formatKwh(Math.max(total, 0))} consumidos`;
}

/** Formatea un monto (`$ 60.000`, moneda COP por defecto). */
export function formatCop(value: number | null | undefined, currency = 'COP'): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  try {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    // Un código ISO inválido no debe romper la tabla.
    return `${value} ${currency}`;
  }
}

/** Formatea un descuento porcentual (`15%`). */
export function formatDiscountPct(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return `${new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)}%`;
}

/** Fecha/hora corta local (es-CO); `—` si falta o es inválida. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Vigencia legible: `desde → hasta` o "Sin fecha de fin". */
export function formatVigencia(
  validFrom: string | null | undefined,
  validTo: string | null | undefined,
): string {
  const from = formatDateTime(validFrom);
  const to = validTo ? formatDateTime(validTo) : 'Sin fecha de fin';
  return `${from} → ${to}`;
}

/** Formatea bytes a una unidad legible (`2,4 MB`). */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes) || bytes < 0) {
    return '—';
  }
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }
  return `${new Intl.NumberFormat('es-CO', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(value)} ${units[unitIndex]}`;
}
